/**
 * Runtime store for the ADS abbreviation list.
 *
 * The DynamoDB-backed `/abbreviations` list is the source of truth. This store
 * holds the in-memory copy that the note expander and casing normalizer read
 * from. It is seeded synchronously from the static ADS_ABBREVIATIONS fallback so
 * expansion works on first paint and offline; the service layer refreshes it
 * from the API after login and after admin edits (see refreshAbbreviationStore).
 *
 * A monotonically increasing version is bumped on every replacement so consumers
 * can cache derived structures (regex patterns, lookups) and rebuild them only
 * when the list actually changes.
 */
import {
  ADS_ABBREVIATIONS,
  ADS_STANDALONE_ABBREVIATIONS,
  AbbreviationEntry,
} from './adsAbbreviations';

/** A store record: an abbreviation, its expansion, and whether it is standalone. */
export interface StoredAbbreviation {
  abbreviation: string;
  expansion: string;
  standalone: boolean;
}

let mainEntries: readonly AbbreviationEntry[] = ADS_ABBREVIATIONS;
let standaloneEntries: readonly AbbreviationEntry[] = ADS_STANDALONE_ABBREVIATIONS;
let version = 0;

/** Current main (multi-character) abbreviation entries. */
export function getMainEntries(): readonly AbbreviationEntry[] {
  return mainEntries;
}

/** Current standalone (single-letter, applied-last) abbreviation entries. */
export function getStandaloneEntries(): readonly AbbreviationEntry[] {
  return standaloneEntries;
}

/**
 * Monotonic version, bumped on every replacement. Consumers cache derived
 * structures keyed on this value and rebuild only when it changes.
 */
export function getStoreVersion(): number {
  return version;
}

/**
 * Replaces the in-memory abbreviation list from the authoritative API list,
 * splitting entries into the main and standalone sets by their `standalone`
 * flag, and bumps the version. Blank abbreviations or expansions are dropped.
 */
export function setAbbreviations(entries: readonly StoredAbbreviation[]): void {
  const main: AbbreviationEntry[] = [];
  const standalone: AbbreviationEntry[] = [];
  for (const entry of entries) {
    const abbreviation = entry.abbreviation.trim();
    const expansion = entry.expansion.trim();
    if (!abbreviation || !expansion) continue;
    const target = entry.standalone ? standalone : main;
    target.push({ abbreviation, expansion });
  }
  mainEntries = main;
  standaloneEntries = standalone;
  version += 1;
}

/**
 * Restores the store to the static seed list and bumps the version. Primarily
 * for tests so each case starts from a known baseline.
 */
export function resetAbbreviationsToSeed(): void {
  mainEntries = ADS_ABBREVIATIONS;
  standaloneEntries = ADS_STANDALONE_ABBREVIATIONS;
  version += 1;
}
